import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useReducer,
} from "react";
import { SplashScreen } from "src/components/splash-screen";
import { WhiteLabelConfig, WhiteLabelProfessionalGroup } from "src/types/snugtotal";
import type { ColorPreset, FontPreset } from "src/theme";

const axios = require("axios").default;

interface WhiteLabelConfigProviderProps {
  children: ReactNode;
}

type WhiteLabelConfigStoreAction =
  | {
      type: "SET_WHITE_LABEL_CONFIG";
      payload: WhiteLabelConfig;
    }
  | {
      type: "UPDATE_BRAND_COLOR_PRESET";
      payload: ColorPreset | null;
    }
  | {
      type: "UPDATE_PROFESSIONAL_GROUP_LOGO";
      payload: Partial<WhiteLabelProfessionalGroup>;
    }
  | {
      type: "UPDATE_FONT_PRESET";
      payload: FontPreset | null;
    };

const whiteLabelConfigReducer = (
  state: WhiteLabelConfig,
  action: WhiteLabelConfigStoreAction
): WhiteLabelConfig => {
  switch (action.type) {
    case "SET_WHITE_LABEL_CONFIG":
      return {
        ...state,
        ...action.payload,
        wl_professional_group: action.payload.professional_group,
      };
    case "UPDATE_BRAND_COLOR_PRESET":
      return {
        ...state,
        wl_professional_group: state.wl_professional_group
          ? {
              ...state.wl_professional_group,
              brand_color_preset: action.payload,
            }
          : state.wl_professional_group,
      };
    case "UPDATE_PROFESSIONAL_GROUP_LOGO":
      return {
        ...state,
        wl_professional_group: state.wl_professional_group
          ? {
              ...state.wl_professional_group,
              ...action.payload,
            }
          : state.wl_professional_group,
      };
    case "UPDATE_FONT_PRESET":
      return {
        ...state,
        wl_professional_group: state.wl_professional_group
          ? {
              ...state.wl_professional_group,
              font_preset: action.payload,
            }
          : state.wl_professional_group,
      };
    default:
      return state;
  }
};

interface WhiteLabelConfigContextValue extends WhiteLabelConfig {
  // Optimistically update the brand color so the theme reacts in real time,
  // without re-fetching the white-label config or refreshing the page.
  updateBrandColorPreset: (preset: ColorPreset | null) => void;
  // Optimistically update the logo / logo size so every surface that reads
  // wl_professional_group (nav, splash, auth layouts) reacts without a refresh.
  updateProfessionalGroupLogo: (logo: Partial<WhiteLabelProfessionalGroup>) => void;
  // Optimistically update the font pairing so the dashboard typography reacts in
  // real time, without re-fetching the white-label config or refreshing.
  updateFontPreset: (preset: FontPreset | null) => void;
}

const WhiteLabelConfigContext = createContext<WhiteLabelConfigContextValue>({
  domain: "",
  disclaimer: null,
  professional_group: null,
  allow_signup: false,
  wl_professional_group: null,
  dashboard_domain: "",
  product_name: "",
  email_sender: "",
  updateBrandColorPreset: () => {},
  updateProfessionalGroupLogo: () => {},
  updateFontPreset: () => {},
});

export const WhiteLabelConfigProvider: FC<WhiteLabelConfigProviderProps> = (
  props
) => {
  const { children } = props;
  const [state, dispatch] = useReducer(whiteLabelConfigReducer, {
    domain: "",
    disclaimer: null,
    professional_group: null,
    allow_signup: false,
    wl_professional_group: null,
    dashboard_domain: "",
    product_name: "",
    email_sender: "",
  });

  useEffect(() => {
    const getWhiteLabelConfig = async () => {
      try {
        const response = await axios.get(
          process.env.REACT_APP_SNUG_API_URL + "/v3/white-label-config/"
        );
        dispatch({
          type: "SET_WHITE_LABEL_CONFIG",
          payload: response.data.data,
        });
      } catch (error) {
        console.error(error);
      }
    };
    getWhiteLabelConfig();
  }, []);

  const updateBrandColorPreset = (preset: ColorPreset | null) => {
    dispatch({ type: "UPDATE_BRAND_COLOR_PRESET", payload: preset });
  };

  const updateProfessionalGroupLogo = (logo: Partial<WhiteLabelProfessionalGroup>) => {
    dispatch({ type: "UPDATE_PROFESSIONAL_GROUP_LOGO", payload: logo });
  };

  const updateFontPreset = (preset: FontPreset | null) => {
    dispatch({ type: "UPDATE_FONT_PRESET", payload: preset });
  };

  if (!state.dashboard_domain) {
    return <SplashScreen />;
  }

  return (
    <WhiteLabelConfigContext.Provider
      value={{
        domain: state.domain,
        disclaimer: "This is a sample disclaimer.",
        professional_group: null, //don't uset this one for clarity downstream.
        allow_signup: state?.allow_signup,
        wl_professional_group: state?.wl_professional_group,
        dashboard_domain: state.dashboard_domain,
        product_name: state.product_name,
        email_sender: state.email_sender,
        updateBrandColorPreset,
        updateProfessionalGroupLogo,
        updateFontPreset,
      }}
    >
      {children}
    </WhiteLabelConfigContext.Provider>
  );
};

export default function useWhiteLabelConfig() {
  return useContext(WhiteLabelConfigContext);
}

export const WhiteLabelConfigConsumer = WhiteLabelConfigContext.Consumer;
